<script setup lang="ts">
import { enUS } from 'date-fns/locale'
import { VueDatePicker as Datepicker } from '@vuepic/vue-datepicker'

import type { PresetDate } from '@vuepic/vue-datepicker'

import { faCalendar } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import { AppText } from '@shared/ui/typography'
import { IconContainer } from '@shared/ui/other'

import { DATE_RANGE_PRESETS } from '../constants/date-range-presets'

interface FilterDateRangeProps {
  label?: string
  placeholder?: string
  enableTimePicker?: boolean
  inputFormat?: string
  /** Lets a lone start date through. Every pick applies at once, so when on, the start applies before the end is picked. */
  partialRange?: boolean
  /** Keeps the start of the given range: only the end can be picked, and neither a preset nor clearing replaces the start. */
  fixedStart?: boolean
  minDate?: Date
  disabled?: boolean
  /** The legacy quick ranges (Today, Last 7 days, This month…) beside the calendar. */
  presets?: boolean
}

defineOptions({
  name: 'FilterDateRange',
})

const props = withDefaults(defineProps<FilterDateRangeProps>(), {
  label: '',
  placeholder: 'Select range',
  enableTimePicker: true,
  inputFormat: 'MMM dd, yyyy HH:mm',
  partialRange: false,
  fixedStart: false,
  minDate: undefined,
  disabled: false,
  presets: true,
})

// The legacy FilterDateTime: two months, every pick applied at once, and the menu stays open to adjust the time.
const PICKER_CONFIG = { closeOnAutoApply: false }

const model = defineModel<Date[] | null>({ default: null })

const presetDates = computed<PresetDate[]>(() => (props.presets && !props.fixedStart ? DATE_RANGE_PRESETS : []))
</script>

<template>
  <div class="filter-date-range">
    <AppText
      v-if="label"
      name="body2"
      color="slate-900"
      class="filter-date-range__label"
    >
      {{ label }}
    </AppText>
    <Datepicker
      v-model="model"
      :range="{ partialRange, fixedStart }"
      :multi-calendars="2"
      auto-apply
      :config="PICKER_CONFIG"
      :min-date="minDate"
      :disabled="disabled"
      :preset-dates="presetDates"
      :time-config="{ timePickerInline: true, enableTimePicker }"
      :locale="enUS"
      :week-start="1"
      :formats="{ input: inputFormat }"
      :input-attrs="{ clearable: !fixedStart }"
      :placeholder="placeholder"
      :teleport-center="true"
    >
      <template #input-icon>
        <IconContainer>
          <FontAwesomeIcon :icon="faCalendar" />
        </IconContainer>
      </template>
    </Datepicker>
  </div>
</template>

<style lang="scss" scoped src="../styles/_filter-date-range.scss" />
